// FAQ 추출 — Tiptap doc 에서 faq 블록의 Q/A 를 평문으로 수집.
//
// 두 렌더러(cms-renderer-astro / cms-renderer-next)가 공유하여 FAQPage
// 구조화데이터(JSON-LD)를 생성한다. 직렬화(HTML <details>)는 각 렌더러가
// 자체 처리(마크업 패리티), 본 모듈은 schema.org 용 평문 추출만 담당.

interface DocNode {
  type?: string;
  text?: string;
  content?: DocNode[];
}

export interface FaqEntry {
  question: string;
  answer: string;
}

/** 노드 트리의 모든 text 를 이어붙여 평문화 (공백 정규화). */
function plainText(node: DocNode | undefined): string {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  if (!Array.isArray(node.content)) return "";
  // 블록 경계는 공백으로 — 문단 사이가 붙지 않도록.
  return node.content.map(plainText).join(" ");
}

function collapse(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/** doc 을 순회하며 faqItem(질문/답변)을 순서대로 수집. */
export function extractFaqEntries(doc: unknown): FaqEntry[] {
  const out: FaqEntry[] = [];
  const walk = (node: DocNode | undefined): void => {
    if (!node || typeof node !== "object") return;
    if (node.type === "faqItem" && Array.isArray(node.content)) {
      const q = node.content.find((c) => c.type === "faqQuestion");
      const a = node.content.find((c) => c.type === "faqAnswer");
      const question = collapse(plainText(q));
      const answer = collapse(plainText(a));
      if (question && answer) out.push({ question, answer });
      return; // faqItem 내부는 더 내려가지 않음
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  if (doc && typeof doc === "object") {
    walk(doc as DocNode);
  }
  return out;
}

/** FAQPage JSON-LD 객체 (script 삽입은 렌더러 책임). null = 항목 없음. */
export function faqPageJsonLd(
  entries: FaqEntry[],
): Record<string, unknown> | null {
  if (entries.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((e) => ({
      "@type": "Question",
      name: e.question,
      acceptedAnswer: { "@type": "Answer", text: e.answer },
    })),
  };
}
